import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router';
import { Users, Loader2, ArrowRight, UserCheck, Wallet } from 'lucide-react';
import { Badge } from '../components/Badge';

interface DashboardStats {
    event: {
        id: number;
        name: string;
        startDate: string;
        endDate: string;
    } | null;
    stats: {
        totalParticipations: number;
        confirmedParticipations: number;
        pendingParticipations: number;
        totalRevenue: number; // in cents
        openBalance: number; // in cents
    };
}

export const RetreatStatsWidget = () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3100/api';

    const { data, isLoading, error } = useQuery<DashboardStats>({
        queryKey: ['retreat-dashboard-stats'],
        queryFn: async () => {
            const res = await axios.get(`${apiUrl}/retreat/dashboard/stats`, { withCredentials: true });
            return res.data;
        },
        refetchInterval: 60000,
    });

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-primary" />
            </div>
        );
    }

    if (error || !data?.event) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 p-4 text-center">
                <Users className="mb-2 opacity-50" size={32} />
                <p className="text-sm font-medium">Keine aktuellen Daten verfügbar</p>
            </div>
        );
    }

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(cents / 100);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
    };

    const confirmedPercent = data.stats.totalParticipations > 0
        ? Math.round((data.stats.confirmedParticipations / data.stats.totalParticipations) * 100)
        : 0;

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Event Info Header */}
            <div className="flex items-center justify-between">
                <div className="flex flex-col">
                    <h4 className="font-bold text-charcoal truncate max-w-[180px]" title={data.event.name}>
                        {data.event.name}
                    </h4>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Aktuelles Event</span>
                </div>
                <Badge variant="neutral" size="sm" shape="pill" className="bg-white/50 border-gray-100">
                    {formatDate(data.event.startDate)}
                </Badge>
            </div>

            <div className="grid grid-cols-1 gap-3 flex-1 overflow-hidden">
                {/* Stats Row 1: Participations */}
                <div className="bg-primary-50/30 rounded-2xl p-4 border border-primary-50 flex items-center gap-4 transition-all hover:bg-primary-50/50">
                    <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
                        <UserCheck size={20} />
                    </div>
                    <div className="flex-1">
                        <div className="text-xl font-black text-charcoal">{data.stats.totalParticipations}</div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Teilnehmer</span>
                            <span className="text-[10px] font-bold text-green-600">{confirmedPercent}% Bestätigt</span>
                        </div>
                    </div>
                </div>

                {/* Stats Row 2: Revenue */}
                <div className="bg-gold-50/30 rounded-2xl p-4 border border-gold-50 flex items-center gap-4 transition-all hover:bg-gold-50/50">
                    <div className="w-10 h-10 rounded-xl bg-gold-600 text-white flex items-center justify-center shadow-lg shadow-gold-600/20">
                        <Wallet size={20} />
                    </div>
                    <div className="flex-1">
                        <div className="text-xl font-black text-charcoal">{formatCurrency(data.stats.totalRevenue)}</div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gesamtumsatz</span>
                            <span className="text-[10px] font-bold text-amber-600">Offen: {formatCurrency(data.stats.openBalance)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <Link
                to="/retreat/registrations"
                className="flex items-center justify-between px-4 py-3 bg-white border border-gray-100 rounded-xl text-sm font-bold text-charcoal hover:bg-gray-50 transition-all group shadow-xs"
            >
                Details ansehen
                <ArrowRight size={16} className="text-gray-300 group-hover:text-primary transition-all group-hover:translate-x-1" />
            </Link>
        </div>
    );
};
